#import "./fragments/dynamicMacroAllocationFragment.graphql"
#import "./fragments/dynamicConstraintFragment.graphql"
#import "./fragments/dynamicMacroAllocationGroupFragment.graphql"
#import "./fragments/dynamicMacroAllocationAllocationGroupFragment.graphql"

query approverForecastMainQueryPart1(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $enableSupplyForecastTargets: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupId
  ) @include(if: $enableSupplyForecastTargets) {
    ...DynamicConstraintFragment
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationFragment
  }
}

query approverForecastMainQueryPart2(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $enableFinancialForecasting: Boolean = false
  $parentIds: [String]
  $loadBudgetTargetGroups: Boolean = false
  $budgetTargetGroupIds: [String]
  $enableMemberCount: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  macroAllocationGroups: getMacroAllocationGroups(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationGroupFragment
    memberCount @include(if: $enableMemberCount)
    budgets {
      allocationProjectId
      targetGroupId
      value
    }
    dailyRate
  }
  relatedSupplyGroups: getGroups(groupIds: $parentIds)
    @include(if: $enableFinancialForecasting) {
    ...DynamicMacroAllocationGroupFragment
    dailyRate
  }
  relatedBudgetTargetGroups: getGroups(groupIds: $budgetTargetGroupIds)
    @include(if: $loadBudgetTargetGroups) {
    ...DynamicMacroAllocationGroupFragment
  }
}

query approverConstraintsMainQueryPart1(
  $groupIdAsString: String!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $skipMacroAllocationQueries: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupIdAsString
  ) {
    ...DynamicConstraintFragment
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupIdAsString
    perspective: $perspective
    enableTypedAllocations: false
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationFragment
  }
}

query approverConstraintsMainQueryPart2(
  $groupIdAsString: String!
  $groupIdAsId: ID!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $enableMemberCount: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  macroAllocationGroups: getMacroAllocationGroups(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupIdAsString
    perspective: $perspective
    enableTypedAllocations: false
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationGroupFragment
    memberCount @include(if: $enableMemberCount)
    # fteSum: no dataloader possible here, this is going to be slow...
    totalCapacityFte: fteSum(
      input: { fteSumType: CAPACITY_FTE, fteSumTarget: ALL_MEMBERS }
    )
    # fteSum: no dataloader possible here, this is going to be slow...
    directMembersCapacityFte: fteSum(
      input: { fteSumType: CAPACITY_FTE, fteSumTarget: DIRECT_MEMBERS }
    )
  }
  rootGroupTotalCapacityFte: getFteSum(
    groupId: $groupIdAsId
    fteSumType: CAPACITY_FTE
    fteSumTarget: ALL_MEMBERS
  )
  rootGroupDirectMembersCapacityFte: getFteSum(
    groupId: $groupIdAsId
    fteSumType: CAPACITY_FTE
    fteSumTarget: DIRECT_MEMBERS
  )
}

query approverPlannerMainQueryPart1(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $referenceDate: Date
  $enablePlannerTargets: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupId
  ) @include(if: $enablePlannerTargets) {
    ...DynamicConstraintFragment
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: false
    referenceDate: $referenceDate
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationFragment
  }
}

query approverPlannerMainQueryPart2(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $referenceDate: Date
  $enablePlannerBudgetTarget: Boolean = false
  $parentIds: [String]
  $enableMemberCount: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  macroAllocationGroups: getMacroAllocationGroups(
    allocationProjectIds: $allocationProjectIds
    approverGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: false
    referenceDate: $referenceDate
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationGroupFragment
    memberCount @include(if: $enableMemberCount)
    budgets {
      allocationProjectId
      targetGroupId
      value
    }
    dailyRate
  }
  relatedSupplyGroups: getGroups(groupIds: $parentIds)
    @include(if: $enablePlannerBudgetTarget) {
    ...DynamicMacroAllocationGroupFragment
    dailyRate
  }
}

query requestorForecastMainQueryPart1(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $skipMacroAllocationQueries: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupId
  ) {
    ...DynamicConstraintFragment
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationFragment
  }
}

query requestorForecastMainQueryPart2(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $includeReferenceDateHistoricGroups: Boolean = false
  $allocationProjectIds: [String]
  $roleAreaTypes: [String]!
  $enableFinancialForecasting: Boolean = false
  $enableMemberCount: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  macroAllocationGroups: getMacroAllocationGroups(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
    includeReferenceDateHistoricGroups: $includeReferenceDateHistoricGroups
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationGroupFragment
    memberCount @include(if: $enableMemberCount)
    dailyRate
  }
  roleAreaGroups: getDescendantGroups(groupTypes: $roleAreaTypes)
    @include(if: $enableFinancialForecasting) {
    ...DynamicMacroAllocationAllocationGroupFragment
    budgets {
      allocationProjectId
      targetGroupId
      value
    }
    dailyRate
    memberCount @include(if: $enableMemberCount)
  }
  relatedSupplyGroups: getRelatedBudgetGroups(groupId: $groupId)
    @include(if: $enableFinancialForecasting) {
    ...DynamicMacroAllocationAllocationGroupFragment
    budgets {
      allocationProjectId
      targetGroupId
      value
    }
    dailyRate
  }
}

query requestorForecastMainQueryForGroupPart1(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $skipMacroAllocationQueries: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupId
  ) {
    ...DynamicConstraintFragment
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
    currentGroupOnly: true
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationFragment
  }
}

query requestorForecastMainQueryForGroupPart2(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $enableTypedAllocations: Boolean = false
  $allocationProjectIds: [String]
  $enableMemberCount: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  macroAllocationGroups: getMacroAllocationGroups(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: $enableTypedAllocations
    currentGroupOnly: true
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationGroupFragment
    memberCount @include(if: $enableMemberCount)
    dailyRate
  }
}

query requestorPlannerMainQueryPart1(
  $groupId: String!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $includeReferenceDateHistoricGroups: Boolean = false
  $referenceDate: Date
  $enablePlannerTargets: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  constraints: getAllocationConstraintsByParentGroupId(
    allocationProjectIds: $allocationProjectIds
    parentGroupId: $groupId
  ) @include(if: $enablePlannerTargets) {
    ...DynamicConstraintFragment
  }
  macroAllocations: getMacroAllocationsAbsoluteValues(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    enableTypedAllocations: false
    includeReferenceDateHistoricGroups: $includeReferenceDateHistoricGroups
    referenceDate: $referenceDate
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationFragment
  }
}

query requestorPlannerMainQueryPart2(
  $groupId: String!
  $roleAreaTypes: [String]!
  $perspective: MacroAllocationQueryPerspective
  $allocationProjectIds: [String]
  $referenceDate: Date
  $enablePlannerBudgetTarget: Boolean = false
  $includeReferenceDateHistoricGroups: Boolean = false
  $enableMemberCount: Boolean = false
  $skipMacroAllocationQueries: Boolean = false
) {
  macroAllocationGroups: getMacroAllocationGroups(
    allocationProjectIds: $allocationProjectIds
    requestorGroupId: $groupId
    perspective: $perspective
    referenceDate: $referenceDate
    includeReferenceDateHistoricGroups: $includeReferenceDateHistoricGroups
    enableTypedAllocations: false
  ) @skip(if: $skipMacroAllocationQueries) {
    ...DynamicMacroAllocationGroupFragment
    objectiveIds
    objectiveVerifiedFor
    linkedStrategies {
      id
      datasource
    }
    memberCount @include(if: $enableMemberCount)
    dailyRate
    attributes {
      id
      attributeType
      subtype
      label
      value
    }
  }
  roleAreaGroups: getDescendantGroups(groupTypes: $roleAreaTypes)
    @include(if: $enablePlannerBudgetTarget) {
    ...DynamicMacroAllocationAllocationGroupFragment
    budgets {
      allocationProjectId
      targetGroupId
      value
    }
    dailyRate
  }
  relatedSupplyGroups: getRelatedBudgetGroups(groupId: $groupId)
    @include(if: $enablePlannerBudgetTarget) {
    ...DynamicMacroAllocationAllocationGroupFragment
    budgets {
      allocationProjectId
      targetGroupId
      value
    }
    dailyRate
  }
}
